import React from 'react';
import styled from 'styled-components';
import { useAppState } from '../../../data';
import pSBC from '../../../utils/pSBC';

const Column = styled.div`
  width: 100%;
  float: left;
  box-sizing: border-box;
  padding: 0;
  display: initial;

  @media (min-width: 550px) {
    margin-left: 4%;

    :first-child {
      margin-left: 0;
    }
  }
`;

const Handler = styled.div<{active: boolean}>`
  cursor: pointer;
  border-left: 10px solid transparent;
  border-bottom: 1px solid #d7d7d7;
  padding-top: 16px;
  padding-right: 5px;
  padding-bottom: 16px;
  padding-left: 12px;
  float: left;

  @media (min-width: 550px) {
    width: 30.6666666667%;
    margin-left: 0;
  }

  @media (max-width: 640px) {
    width: 100%;
  }

  ${props => props.active ? `
    border-left: 10px solid ${props.color};
    background-color: #fff;
    border-bottom: none;
    position: relative;
  `
  :
  ``}
`;

const Title = styled.h3`
  color: #1C1C1C;
  float: left;
  text-align: left;
  margin: 0;
  line-height: 1.4;
  font-size: 1em;
  word-break: break-word;
  word-wrap: break-word;
  font-weight: 600;
`;

const DescriptionContainer = styled.div`
  word-break: break-word;
  word-wrap: break-word;
  padding-top: 20px;
  padding-right: 16px;
  padding-bottom: 15px;
  position: absolute;
  left: 30%;
  float: left;

  @media (min-width: 550px) {
    width: 65.3333333333%;
    margin-left: 4%;
  }

  @media (max-width: 640px) {
    margin: 0;
    padding-top: 0;
    padding-right: 20px;
    padding-bottom: 15px;
    padding-left: 20px;
    position: relative;
    left: auto;
  }

  @media (max-width: 640px) {
    width: 100%;
    margin: 0;
  }
`;

const GroupToggle = styled.div`
  display: inline-block;
  width: 100%;
  margin-bottom: 10px;

  @media (max-width: 640px), (max-height: 640px) and (max-width: 896px) and (orientation: landscape) {
    margin-left: 10px;
    position: absolute;
    right: 10px;
    top: -37px;
  }
`;

const Header = styled.h3`
  color: #1C1C1C;
  float: left;
  margin: 0;
  font-weight: bold;
  font-size: 1em;
  line-height: 1.3;
  max-width: 85%;
  top: 3px;

  @media (max-width: 640px), (max-height: 640px) and (max-width: 896px) and (orientation: landscape) {
    display: none;
  }
`;

const ToogleGroup = styled.div`
  float: right;
  display: inline-block;
  vertical-align: middle;
`;

const Toogle = styled.div`
  float: right;
  background-color: #fff;
  display: inline-block;
  vertical-align: middle;
`;

const Checkbox = styled.div`
  background-color: #fff;
  position: relative;
  display: inline-block;
`;

const Input = styled.input<{color: string}>`
  display: inline;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 1;
  pointer-events: initial;

  :checked + label {
    background: ${props => pSBC(0.6, props.color)};
  }

  :checked + label:after {
    background: ${props => props.color};
    left: 16px;
  }
`;

const Label = styled.label`
  font-weight: 600;
  margin-bottom: 0;
  background: #d6d6d6;
  border: none;
  height: 10px;
  width: 35px;
  position: relative;
  display: inline-block;
  border-radius: 46px;
  transition: .4s;

  ::after {
    content: "";
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 100%;
    left: 0;
    top: -5px;
    z-index: 2;
    background: #7b7b7b;
    transition: .4s;
    cursor: pointer;
  }
`;

const Description = styled.p`
  word-break: break-word;
  word-wrap: break-word;
  text-align: left;
  font-size: .82em;
  line-height: 1.3;
  margin: 0;
  color: #1C1C1C;
`;

const Link = styled.a`
  font-size: .8em;
  font-weight: bold;
  line-height: 1.1;
  cursor: pointer;
  margin: 1em 0;
  color: ${props => props.color};
`;

export interface CookieDetailsProps {
  active: boolean;
  title: string;
  description: string;
  index?: number;
  required?: boolean;
  hideCookies?: boolean;
  onClick?(index?: number): void;
}

export default function CookieDetails({ index, active, title, description, required, onClick, hideCookies = false }: CookieDetailsProps) {
  const identifier = `sb-lgpd-cd-${index}`;
  const { setShowVendors, appConfig, getTranslation } = useAppState();
  
  return (
    <>
      <Column as="ldiv">
        <Handler color={appConfig.UIConfig.buttonBackgroundColor} as="ldiv" active={active} onClick={() => onClick && onClick(index)}>
          <Title as="ldiv">{title}</Title>
        </Handler>
      </Column>
      <DescriptionContainer as="ldiv" style={{display: active ? 'initial' : 'none'}}>
        {index !== undefined ?
          <GroupToggle as="ldiv">
            <Header as="ldiv">{title}</Header>
            <ToogleGroup as="ldiv">
              {required ?
                <>
                  <input type="checkbox" style={{display: 'none'}} name={identifier} id={identifier} defaultChecked={true} />
                  <div style={{fontSize: '1em', lineHeight: '1.3', color: appConfig.UIConfig.buttonBackgroundColor}}>{getTranslation('COOKIE_DETAILS_ALWAYS_ACTIVE')}</div>
                </>
              :
                <Toogle as="ldiv">
                  <Checkbox as="ldiv">
                    <Input color={appConfig.UIConfig.buttonBackgroundColor} type="checkbox" role="switch" id={identifier} name={identifier} defaultChecked={required}/>
                    <Label htmlFor={identifier}></Label>
                  </Checkbox>
                </Toogle>
              }
            </ToogleGroup>
          </GroupToggle>
        :
          <Header as="ldiv" style={{float: 'initial', marginBottom: '14px'}}>{title}</Header>
        }
        <Description as="ldiv">{description}</Description>
        {!hideCookies && <Link as="ldiv" color={appConfig.UIConfig.buttonBackgroundColor} onClick={() => setShowVendors(index)}>{getTranslation('COOKIE_DETAILS_COOKIES')}</Link>}
      </DescriptionContainer>
    </>
  );
}